<template>
  <div :class="['message', message.type]">
    <template v-if="message.type === 'ai'">
      <div v-if="thinkingTime > 0" class="loading-dots">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <div 
        v-if="message.thinkingTime || (message.reasoning && message.reasoning.trim())" 
        class="reasoning"
        :class="{ collapsed }"
      >
        <div class="reasoning-header">
          <span class="thinking-time">思考 {{ message.thinkingTime || thinkingTime }}秒</span>
          <button class="toggle-button" @click="toggleCollapse">
            <svg class="toggle-icon" :class="{ collapsed }" viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
            </svg>
          </button>
        </div>
        <div class="reasoning-content">
          {{ message.reasoning }}
        </div>
      </div>
      <div v-if="message.content && message.content.trim()" class="content-wrapper">
        <!-- 内容区 -->
        <div class="content markdown-body" v-html="renderedContent"></div>
        
        <!-- 导出按钮，仅在内容完全加载完成后显示 -->
        <div v-if="message.content && !thinkingTime && !isStreaming" class="export-container">
          <button class="export-button" @click="toggleExportMenu">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M5 20h14v-2H5v2zM12 4L7 9h4v7h2V9h4L12 4z"/>
            </svg>
            导出
          </button>
          <div v-if="showExportMenu" class="export-menu">
            <button @click="exportToWord">导出Word</button>
            <button @click="copyWithFormat">复制文字（含格式）</button>
            <button @click="copyPlainText">复制文字（仅文字）</button>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="content">{{ message.content }}</div>
    </template>
    <Toast ref="toast" />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { renderMarkdown } from '../utils/markdownConfig';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import Toast from './Toast.vue';

const props = defineProps({
  message: {
    type: Object,
    required: true
  },
  thinkingTime: {
    type: Number,
    default: 0
  },
  isStreaming: {
    type: Boolean,
    default: false
  }
});

// Toast组件引用
const toast = ref(null);

// 默认展开状态
const collapsed = ref(false);
// 控制导出菜单显示状态
const showExportMenu = ref(false);

const renderedContent = computed(() => {
  if (props.message.type === 'user') return props.message.content;
  return renderMarkdown(props.message.content || '');
});

// 监听思考过程的变化
watch(() => props.message.reasoning, (newVal) => {
  // 只在思考过程完全结束时自动折叠
  if (newVal && props.message.content) {
    // 思考过程已完成且回复内容开始显示时，延迟折叠
    setTimeout(() => {
      collapsed.value = true;
    }, 1000);
  }
}, { immediate: true });

const toggleCollapse = () => {
  collapsed.value = !collapsed.value;
};

// 切换导出菜单显示状态
const toggleExportMenu = () => {
  showExportMenu.value = !showExportMenu.value;
};

// 关闭导出菜单的方法
const closeExportMenu = () => {
  showExportMenu.value = false;
};

// 将Markdown转换为纯文本的工具函数
const removeMarkdown = (text) => {
  if (!text) return '';
  
  return text
    .replace(/#{1,6}\s/g, '') // 移除标题标记
    .replace(/\*\*/g, '') // 移除粗体标记
    .replace(/\*/g, '') // 移除斜体标记
    .replace(/`{1,3}/g, '') // 移除代码标记
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // 替换链接为纯文本
    .replace(/!\[([^\]]+)\]\([^\)]+\)/g, '') // 移除图片
    .replace(/^>\s/gm, '') // 移除引用标记
    .replace(/^[0-9]+\.\s/gm, '') // 移除有序列表标记
    .replace(/^-\s/gm, '') // 移除无序列表标记
    .replace(/\n{2,}/g, '\n\n'); // 规范化多余的换行符
};

// 导出为Word文档
const exportToWord = async () => {
  try {
    // 创建段落数组
    const children = [];
    
    // 先将Markdown内容转换为纯文本
    const plainText = removeMarkdown(props.message.content);
    
    // 处理文本内容，将纯文本分割为段落
    const paragraphs = plainText.split('\n\n').filter(p => p.trim());
    
    // 将每个段落添加到文档中
    paragraphs.forEach(paragraph => {
      if (!paragraph.trim()) return;
      
      children.push(new Paragraph({
        children: [new TextRun(paragraph)],
        spacing: { after: 200 }
      }));
    });
    
    // 创建Word文档对象，添加必要的文档属性和内容
    const doc = new Document({
      creator: "特教求索",
      title: "AI回复内容",
      description: "由DeepSeek R1生成的回复",
      sections: [{
        properties: {},
        children: children
      }]
    });
    
    // 生成Word文档的二进制数据
    const buffer = await Packer.toBlob(doc);
    
    // 使用file-saver库保存文件
    saveAs(buffer, `ai-response-${new Date().toISOString().slice(0,10)}.docx`);
    
    // 显示成功提示
    toast.value?.showToast({
      message: 'Word文档已下载',
      type: 'success'
    });
    
    // 关闭菜单
    closeExportMenu();
  } catch (error) {
    console.error('导出Word文档失败:', error);
    toast.value?.showToast({
      message: '导出Word文档失败，请重试',
      type: 'error'
    });
  }
};

// 复制带格式的文本
const copyWithFormat = async () => {
  try {
    await navigator.clipboard.writeText(props.message.content);
    
    toast.value?.showToast({
      message: '已复制Markdown格式文本到剪贴板',
      type: 'success'
    });
    closeExportMenu();
  } catch (error) {
    console.error('复制失败:', error);
    toast.value?.showToast({
      message: '复制失败，请重试',
      type: 'error'
    });
  }
};

// 复制纯文本（去除Markdown格式）
const copyPlainText = async () => {
  try {
    // 简单的Markdown格式移除
    const plainText = removeMarkdown(props.message.content);
    
    await navigator.clipboard.writeText(plainText);
    
    toast.value?.showToast({
      message: '已复制纯文本内容到剪贴板',
      type: 'success'
    });
    closeExportMenu();
  } catch (error) {
    console.error('复制失败:', error);
    toast.value?.showToast({
      message: '复制失败，请重试',
      type: 'error'
    });
  }
};

// 在组件外部点击时关闭导出菜单
watch(() => showExportMenu.value, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      window.addEventListener('click', handleOutsideClick);
    }, 0);
  } else {
    window.removeEventListener('click', handleOutsideClick);
  }
});

const handleOutsideClick = (event) => {
  const exportContainer = event.target.closest('.export-container');
  if (!exportContainer) {
    closeExportMenu();
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.content-wrapper {
  position: relative;
  display: inline-block;
  max-width: 80%;
}

.export-container {
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 2;
}

.export-button {
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: transparent;
  color: rgba($primary-color, 0.7);
  border: 1px solid rgba($primary-color, 0.3);
  border-radius: $border-radius-sm;
  padding: 4px 8px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba($primary-color, 0.05);
    color: $primary-color;
    border-color: rgba($primary-color, 0.5);
  }
  
  svg {
    margin-right: 4px;
  }
}

.export-menu {
  position: absolute;
  bottom: 100%; // 改为顶部显示
  right: 0;
  margin-bottom: 4px; // 调整间距
  background-color: white;
  border-radius: $border-radius-sm;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 10;
  overflow: hidden;
  min-width: 180px;
  
  button {
    display: block;
    width: 100%;
    text-align: left;
    padding: 8px 12px;
    border: none;
    background-color: transparent;
    cursor: pointer;
    color: $text-color;
    font-size: 0.9rem;
    
    &:hover {
      background-color: rgba($primary-color, 0.05);
    }
    
    &:not(:last-child) {
      border-bottom: 1px solid rgba($border-color, 0.5);
    }
  }
}

.message {
  margin-bottom: $spacing-md;
  
  &.user {
    margin-left: auto;
    text-align: right;
    
    .content {
      display: inline-block;
      text-align: left;
      max-width: 80%;
      background-color: $user-msg-bg;
      color: $user-msg-text;
      border-radius: $border-radius-md 0 $border-radius-md $border-radius-md;
      padding: $spacing-md;
    }
  }

  &.ai {
    margin-right: auto;
    
    .content {
      display: inline-block;
      width: 100%;
      background-color: #ffffff;
      color: $ai-msg-text;
      border-radius: 0 $border-radius-md $border-radius-md $border-radius-md;
      border: 1px solid rgba(#4A90E2, 0.2);
      padding: $spacing-md $spacing-lg $spacing-xl $spacing-lg; // 增加底部内边距，为导出按钮留出空间
      box-shadow: 0 2px 12px rgba(#4A90E2, 0.08);
      position: relative;
      
      // 添加微妙的渐变背景
      background: linear-gradient(
        to bottom right,
        #ffffff,
        rgba(#4A90E2, 0.02)
      );
      
      // 添加左侧装饰条
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        background: linear-gradient(to bottom, #4A90E2, #5B9EE6);
        border-radius: $border-radius-md 0 0 $border-radius-md;
        opacity: 0.6;
      }
    }
  }
}

.reasoning {
  background-color: $thinking-bg;
  border-radius: $border-radius-md;
  margin-bottom: $spacing-sm;
  overflow: hidden;
  transition: all 0.3s ease;

  .reasoning-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: $spacing-sm $spacing-md;
    border-bottom: 1px solid rgba($border-color, 0.5);
  }

  .thinking-time {
    font-size: 0.9em;
    color: $thinking-text;
  }

  .toggle-button {
    padding: $spacing-xs;
    border: none;
    background-color: transparent;
    color: $primary-color;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: 24px;
    height: 24px;

    &:hover {
      background-color: rgba($primary-color, 0.1);
    }

    .toggle-icon {
      transition: transform 0.3s ease;
      
      &.collapsed {
        transform: rotate(-180deg);
      }
    }
  }

  .reasoning-content {
    padding: $spacing-md;
    color: $thinking-text;
    font-size: 0.9em;
    transition: all 0.3s ease;
    max-height: none;
    white-space: pre-wrap;
    line-height: 1.6;
  }

  &.collapsed .reasoning-content {
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
    overflow: hidden;
  }
}

.content {
  padding: $spacing-md;
  word-wrap: break-word;
}

:deep(.markdown-body) {
  font-size: 1rem;
  line-height: 1.8;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  color: #2c3e50;
  
  p {
    margin: 1em 0;
    color: $text-color;
    
    &:first-child {
      margin-top: 0;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 1.5em 0 1em;
    font-weight: 600;
    line-height: 1.4;
    
    &:first-child {
      margin-top: 0;
    }
  }

  ul, ol {
    margin: 1em 0;
    padding-left: 2em;
    
    li {
      margin: 0.5em 0;
    }
  }

  code {
    font-family: ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace;
    padding: 0.2em 0.4em;
    font-size: 0.9em;
    background-color: rgba(#4A90E2, 0.06);
    border-radius: 3px;
    color: #476582;
  }

  pre {
    margin: 1em 0;
    padding: 1em;
    background-color: #f8fafc;
    border-radius: $border-radius-sm;
    border: 1px solid rgba(#4A90E2, 0.1);
    overflow-x: auto;
    
    code {
      padding: 0;
      background-color: transparent;
      color: #476582;
    }
  }

  blockquote {
    margin: 1em 0;
    padding: 0.5em 1em;
    color: #476582;
    border-left: 0.25em solid #4A90E2;
    background-color: rgba(#4A90E2, 0.03);
    border-radius: 0 $border-radius-sm $border-radius-sm 0;
  }

  @media (max-width: 768px) {
    line-height: 1.95;
    
    ul, ol {
      padding-left: 1.2em;
      
      li {
        margin: 0.35em 0;
      }
    }

    ul ul, ol ol {
      padding-left: 0.9em;
      margin-left: 0.25em;
      border-left: 2px solid #e2e8f0;
      padding-top: 0.2em;
    }
  }
}

.loading-dots {
  display: inline-flex;
  align-items: center;
  padding: $spacing-sm $spacing-md;
  background-color: $thinking-bg;
  border-radius: $border-radius-md;
  gap: 4px;
  
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: $primary-color;
    opacity: 0.6;
    animation: dot-flashing 1s infinite alternate;
    
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes dot-flashing {
  0% {
    opacity: 0.2;
    transform: scale(0.8);
  }
  100% {
    opacity: 0.8;
    transform: scale(1);
  }
}
</style> 
